import type { CensusWorkbookReceipt } from "@kollectivia/shared";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { api } from "../../../lib/api";

type Workbook = CensusWorkbookReceipt;

export function ReceivedCensusWorkbooks({
  censusId,
  onReview,
}: {
  readonly censusId: string;
  readonly onReview?: (fileId: string) => void;
}) {
  const { t } = useTranslation();
  const query = useQuery({
    queryKey: ["census-workbooks", censusId],
    queryFn: () => api.get<{ items: Workbook[] }>(`/v1/censuses/${censusId}/workbooks`),
    refetchInterval: 30_000,
    refetchOnWindowFocus: "always",
  });
  return (
    <section
      className="my-4 rounded-lg border border-slate-200 p-3"
      aria-label={t("census.handoff.received_title")}
    >
      <h4 className="font-medium">{t("census.handoff.received_title")}</h4>
      {query.isPending ? <p role="status">{t("census.broker.loading")}</p> : null}
      {query.isError ? (
        <p role="alert">
          {t("census.handoff.read_error")}{" "}
          <button type="button" className="min-h-11 underline" onClick={() => void query.refetch()}>
            {t("census.broker.retry")}
          </button>
        </p>
      ) : null}
      {query.data?.items.length === 0 ? (
        <p className="text-sm text-slate-600">{t("census.handoff.none")}</p>
      ) : null}
      <ul className="space-y-2">
        {query.data?.items.map((file) => (
          <li key={file.id} className="flex flex-wrap items-center justify-between gap-2 text-sm">
            <span className="min-w-0 break-all">
              {file.file_name} —{" "}
              {t(file.imported ? "census.handoff.imported" : "census.handoff.received")}
            </span>
            {onReview ? (
              <button
                type="button"
                className="focus-visible:ring-brand-500 min-h-11 rounded border px-3 py-2 focus-visible:ring-2"
                onClick={() => onReview(file.id)}
              >
                {t("census.handoff.review")}
              </button>
            ) : null}
          </li>
        ))}
      </ul>
      <p className="mt-2 text-xs text-slate-600">{t("census.handoff.review_hint")}</p>
    </section>
  );
}
